/**
 * Grade processing and upload page.
 * Processes NeoLMS grades per subject and uploads to WELA with confirmation.
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { gradesService } from '../services/api.service';
import { ProcessGradesResponse, GradeData } from '../types/grade.types';

interface LocationState {
  schoolYear: string;
  quarter: string;
  className: string;
  classData: any;
  subjects: Array<{
    subject: string;
    subject_id: string;
    teacher: string;
    neolms_id: string;
    selected: boolean;
  }>;
}

interface SubjectGradeData {
  subject: string;
  subject_id: string;
  teacher: string;
  neolms_id: string;
  processedData: ProcessGradesResponse | null;
  loading: boolean;
  error: string | null;
  uploadSuccess: boolean;
}

/**
 * Step 3 of the workflow: preview and upload processed grades to WELA.
 */
const GradeProcessing: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { schoolYear, quarter, className, classData } = location.state as LocationState || {};
  
  const [subjectsData, setSubjectsData] = useState<SubjectGradeData[]>([]);
  const [globalLoading, setGlobalLoading] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState<number>(-1);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState<string>('');
  
  useEffect(() => {
    // Redirect back if no class was selected
    if (!className || !location.state) {
      navigate('/class-selection');
      return;
    }
    
    const { subjects } = location.state as LocationState;
    if (!subjects || subjects.length === 0) {
      navigate('/class-selection');
      return;
    }
    
    // Set up subject data structure
    const selectedSubjects = subjects.filter(subject => subject.selected);
    const initialSubjectsData: SubjectGradeData[] = selectedSubjects.map(subject => ({
      subject: subject.subject,
      subject_id: subject.subject_id,
      teacher: subject.teacher,
      neolms_id: subject.neolms_id,
      processedData: null,
      loading: false,
      error: null,
      uploadSuccess: false
    }));
    
    setSubjectsData(initialSubjectsData);
    
    // Only process grades if we have subjects loaded
    if (initialSubjectsData.length > 0) {
      // We'll process grades after initialSubjectsData is set in state
      setTimeout(() => {
        processAllSubjects();
      }, 0);
    }
  }, [className, navigate, location.state]);
  
  const processAllSubjects = async () => {
    // Get the current data through the state updater to ensure we have the latest value
    setSubjectsData(prevSubjects => {
      // First mark all as loading
      const updatedSubjects = prevSubjects.map(subject => ({
        ...subject,
        loading: true,
        error: null
      }));
      
      // Start processing in the background
      processSubjectsData(updatedSubjects);
      
      // Return the updated subjects with loading state
      return updatedSubjects;
    });
  };
  
  // Separate function to process subjects data after state update
  const processSubjectsData = async (subjects: SubjectGradeData[]) => {
    const updatedSubjects = [...subjects];
    
    // Process each subject individually
    for (let i = 0; i < updatedSubjects.length; i++) {
      try {
        const subject = updatedSubjects[i];
        // Convert NeoLMS ID to a number
        const neolmsClassId = parseInt(subject.neolms_id, 10);
        if (isNaN(neolmsClassId) || neolmsClassId <= 0) {
          throw new Error(`Invalid NeoLMS ID: ${subject.neolms_id}`);
        }
        
        const response = await gradesService.processGrades(
          className, 
          neolmsClassId, 
          quarter
        );
        
        // Update with processed data
        updatedSubjects[i] = {
          ...subject,
          processedData: response,
          loading: false
        };
      } catch (error) {
        console.error(`Error processing grades for ${updatedSubjects[i].subject}:`, error);
        updatedSubjects[i] = {
          ...updatedSubjects[i],
          error: 'Failed to process grades. Please try again.',
          loading: false
        };
      }
      
      // Update state after each subject is processed
      setSubjectsData([...updatedSubjects]);
    }
  };
  
  const handleUploadGrades = async (index: number) => {
    const subject = subjectsData[index];
    
    if (!subject || !subject.processedData) return;
    
    // Update loading state
    setSubjectsData(prevData => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], loading: true };
      return newData;
    });
    
    try {
      await gradesService.uploadGrades({
        class_name: className,
        subject_id: subject.subject_id,
        quarter: quarter,
        neolms_class_id: parseInt(subject.neolms_id, 10)
      });
      
      // Update success state
      setSubjectsData(prevData => {
        const newData = [...prevData];
        newData[index] = { ...newData[index], uploadSuccess: true, loading: false };
        return newData;
      });
      
      // Show confirmation dialog
      setCurrentUploadIndex(index);
      setOpenConfirmDialog(true);
      
    } catch (err: any) {
      // Update error state
      setSubjectsData(prevData => {
        const newData = [...prevData];
        newData[index] = { 
          ...newData[index], 
          error: err.message || 'Failed to upload grades', 
          loading: false 
        };
        return newData;
      });
    }
  };
  
  const handleDialogClose = () => {
    setOpenConfirmDialog(false);
    setCurrentUploadIndex(-1);
  };
  
  const handleBackToClassSelection = () => {
    navigate('/class-selection', { 
      state: { 
        schoolYear, 
        quarter 
      } 
    });
  };

  const handleRefreshAllGrades = () => {
    processAllSubjects();
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Process Grades
        </Typography>
        
        <Typography variant="body1" paragraph align="center" sx={{ mb: 2 }}>
          {schoolYear} - {quarter} - {className}
        </Typography>
        
        {globalLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Processing grades for all subjects...
            </Typography>
          </Box>
        ) : (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            {subjectsData.length === 0 ? (
              <Alert severity="warning">
                No subjects selected. Please go back and select subjects to process.
              </Alert>
            ) : (
              <>
                {subjectsData.map((subject, index) => (
                  <Accordion key={index} defaultExpanded={index === 0}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`subject-${index}-content`}
                      id={`subject-${index}-header`}
                    >
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {subject.subject} <Typography variant="caption">({subject.teacher})</Typography>
                      </Typography>
                      
                      {subject.uploadSuccess && (
                        <Chip 
                          label="Uploaded" 
                          color="success" 
                          size="small" 
                          sx={{ mr: 2 }}
                        />
                      )}
                      
                      {subject.error && (
                        <Chip 
                          label="Error" 
                          color="error" 
                          size="small" 
                          sx={{ mr: 2 }}
                        />
                      )}
                      
                      {subject.loading && (
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      {subject.error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                          {subject.error}
                        </Alert>
                      )}
                      
                      {subject.loading ? (
                        <Box display="flex" justifyContent="center" padding={3}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <>
                          <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">
                                Subject:
                              </Typography>
                              <Typography variant="body1">
                                {subject.subject}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">
                                Teacher:
                              </Typography>
                              <Typography variant="body1">
                                {subject.teacher}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">
                                NeoLMS ID:
                              </Typography>
                              <Typography variant="body1">
                                {subject.neolms_id}
                              </Typography>
                            </Grid>
                          </Grid>
                          
                          {subject.processedData && subject.processedData.grades.length > 0 ? (
                            <>
                              <Typography variant="body2" paragraph>
                                {subject.processedData.message}
                              </Typography>
                              
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>ID</TableCell>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Gender</TableCell>
                                      <TableCell>Grade Level</TableCell>
                                      <TableCell>Grade</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {subject.processedData.grades.map((student, studentIndex) => (
                                      <TableRow key={studentIndex}>
                                        <TableCell>{student.id_num}</TableCell>
                                        <TableCell>{student.full_name}</TableCell>
                                        <TableCell>{student.gender}</TableCell>
                                        <TableCell>{student.grade_level}</TableCell>
                                        <TableCell>{student.grade}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              
                              <Box mt={2} display="flex" justifyContent="flex-end">
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleUploadGrades(index)}
                                  disabled={subject.loading || subject.uploadSuccess}
                                >
                                  {subject.uploadSuccess ? 'Uploaded' : 'Upload Grades'}
                                </Button>
                              </Box>
                            </>
                          ) : (
                            <Alert severity="info">
                              No grades found for this subject and quarter.
                            </Alert>
                          )}
                        </>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
                
                <Divider sx={{ my: 3 }} />
                
                <Box display="flex" justifyContent="space-between" mt={3}>
                  <Button
                    variant="outlined"
                    onClick={handleBackToClassSelection}
                  >
                    Back to Class Selection
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRefreshAllGrades}
                    disabled={subjectsData.some(sg => sg.loading)}
                  >
                    Refresh All Grades
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleDialogClose}
      >
        <DialogTitle>Grades Uploaded Successfully</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {currentUploadIndex >= 0 && subjectsData[currentUploadIndex] && 
              `The grades for ${subjectsData[currentUploadIndex].subject} have been successfully uploaded to WELA.`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GradeProcessing;